import type {Repository} from '@github-ui/current-repository'
import type {NavigationUrls} from '../../types/navigation-urls-types'
import type {MergeabilityLiveUpdateChannels} from '@github-ui/mergebox/use-mergeability-live-updates'
import type {MergeMethod} from '@github-ui/mergebox/types'
import type {PullRequest, PullRequestState} from '../../types'
import type {PendingReview} from '../../stores/slices/pending-review'
import type {PullRequestNotices, UserNotice} from './notices'
import type {DiffstatResponse} from '@github-ui/dotcom-schema/types/pull_requests/v1/diffstat'
import type {CommitMessageGenerationConfig} from '@github-ui/diff-lines/conversation-types'

export type OnboardingBannerProps = {
  dismissNoticePath: string
  helpURL: string
  repoSettingsPath: string
  showOptOut: boolean
}

export type DependabotAutomatedSecurityUpdates = {
  render: boolean
  alertPresent: boolean
  packageName: string
  singleAlert: boolean
  securityAlertPath: string
  severity: string
  showOnboardingPopover: boolean
  onboardingBannerProps: OnboardingBannerProps
}

export type ReviewRequestBanner =
  | {
      render: false
    }
  | {
      render: true
      requestedBySelf: boolean
      requestingUserDisplayLogin: string
    }

type BannersType = {
  banners: {
    dependabotAutomatedSecurityUpdates: DependabotAutomatedSecurityUpdates
    pausedDependabotUpdate: {render: boolean}
    hiddenCharacterWarning: {render: boolean}
    reviewRequestBanner: ReviewRequestBanner
    canStackBanner: {render: boolean}
  }
}

export type MergeStatusButtonData = {
  channels: MergeabilityLiveUpdateChannels
  defaultMergeMethod: MergeMethod
  headSha: string
  headShaChannel: string
  helpUrl: string
  viewerLogin?: string
}

export type AsyncStackData = {
  id: number
  stack: StackData
}

export type StackData = {
  allPullsLocked?: boolean
  baseBranch: string
  createdAt: string
  id: number
  number: number
  repositoryOwnerLogin: string
  repositoryName: string
  position: number
  size: number
  pulls: Array<{
    headBranch: string
    isUnstackable: boolean
    title: string
    number: number
    state: PullRequestState
    url: string
  }>
}

export type AliveChannels = {
  pullRequest: string
  pullRequestState: string
  pullRequestTimeline: string
}

export type ViewSettings = {
  dockedPanelWidth: number | null
}

export type HeaderPageData = {
  aliveChannels: AliveChannels
  pageTitle: string
  bannersData: BannersType
  featureStates?: FeatureStates
  helpUrl: string
  pullRequest: PullRequest
  repository: HeaderRepository
  urls: NavigationUrls
  user: User
  mergeStatusButtonData?: MergeStatusButtonData
  refListCacheKey?: string
  stack?: StackData | null
  viewerPendingReview?: PendingReview
  viewSettings: ViewSettings
  /**
   * Backend-authorized, viewer + PR scoped config for generating a commit title and description for an automated
   * (CCR) suggestion, served layout-wide on the PR header/layout route payload. Absent/null when
   * the backend has not authorized generation (kill switch, entitlement, or endpoint restriction
   * not met). Consumed by the modern React Files Changed/Overview surfaces via
   * the layout store. Classic Rails-hydrated islands instead read the equivalent per-comment
   * `AutomatedComment.commitMessageGenerationConfig` field, since they cannot read this route
   * payload -- see the precedence resolution in `AutomatedReviewSuggestion.tsx`.
   */
  commitMessageGenerationConfig?: CommitMessageGenerationConfig | null
}

export type DiffstatData = DiffstatResponse

export type HeaderRepository = Repository & {
  slashCommandsEnabled?: boolean
  codespacesEnabled: boolean
  isEnterprise: boolean
  editorEnabled: boolean
  userNameDisplayConfiguration?: string | null
}

export interface User {
  canChangeBase: boolean
  canComment?: boolean
  canEditTitle: boolean
  canFixWithCopilot?: boolean | null
  notices?: Array<UserNotice<PullRequestNotices>>
}

export const FeatureState = {
  Enabled: 'ENABLED',
  Available: 'AVAILABLE',
  Unavailable: 'UNAVAILABLE',
} as const

export type FeatureState = (typeof FeatureState)[keyof typeof FeatureState]

export interface FeatureStates {
  agentMerge?: FeatureState
  findings?: FeatureState
  overview?: FeatureState
  pullRequestStacks?: FeatureState
  pullRequestStacksAsyncMerge?: FeatureState
  pullRequestStacksStatusesV2?: FeatureState
}
